// Iter 84.1 / Phase E5 Sprint 1 — Compliance Console.
// Single page with tabs: PF ECR · ESI Return · Settings · Coverage.
// Selects payroll runs → preview → generate → seal (4-eye) → download.
import { useCallback, useEffect, useState } from "react";
import {
    DownloadSimple, Eye, ArrowsClockwise, ShieldCheck, FloppyDisk,
    Sparkle, Warning, CheckCircle,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";

const fmtMoney = (n) =>
    `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const truncHash = (h) => (h ? `${h.slice(0, 10)}…${h.slice(-6)}` : "");

const TABS = [
    { key: "pf", label: "PF ECR" },
    { key: "esi", label: "ESI Return" },
    { key: "pt", label: "PT Challan" },
    { key: "lwf", label: "LWF Return" },
    { key: "settings", label: "Settings" },
    { key: "coverage", label: "Coverage" },
];

// Iter 84.2 / Phase E5 Sprint 2 — D34 / D35 state coverage.
const PT_STATES = ["Maharashtra", "Karnataka", "West Bengal", "Tamil Nadu", "Telangana", "Andhra Pradesh"];
const LWF_STATES = ["Maharashtra", "Karnataka", "Tamil Nadu", "Gujarat", "Andhra Pradesh"];

export default function ComplianceConsole() {
    const { can } = useAuth();
    const canAdmin = can("compliance:admin");
    const canSeal = can("compliance:seal");
    const [tab, setTab] = useState("pf");
    const [meta, setMeta] = useState(null);
    const [settings, setSettings] = useState(null);
    const [eligibleRuns, setEligibleRuns] = useState([]);
    const [history, setHistory] = useState([]);
    const [summary, setSummary] = useState(null);
    const [coverage, setCoverage] = useState(null);
    // Builder
    const [period, setPeriod] = useState("");
    const [selectedRuns, setSelectedRuns] = useState([]);
    const [preview, setPreview] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    // Iter 84.2 / Phase E5 Sprint 2 — PT / LWF state selectors.
    const [ptState, setPtState] = useState(PT_STATES[0]);
    const [lwfState, setLwfState] = useState(LWF_STATES[0]);

    const reload = useCallback(async () => {
        try {
            const [m, s, e, h, d, c] = await Promise.all([
                api.get("/compliance/meta"),
                api.get("/compliance/settings"),
                api.get("/compliance/eligible-payroll-runs?limit=50"),
                api.get("/compliance/filings?limit=50"),
                api.get("/compliance/dashboard/summary"),
                api.get("/compliance/employee-statutory-coverage"),
            ]);
            setMeta(m.data);
            setSettings(s.data);
            setEligibleRuns(e.data.items || []);
            setHistory(h.data.items || []);
            setSummary(d.data);
            setCoverage(c.data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load compliance data"));
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const toggleRun = (runId) => {
        setSelectedRuns((prev) =>
            prev.includes(runId) ? prev.filter((r) => r !== runId) : [...prev, runId]
        );
    };

    const handlePreview = async (form) => {
        if (!period || selectedRuns.length === 0) {
            toast.error("Set period (YYYY-MM) and select at least 1 payroll run");
            return;
        }
        // Iter 84.2 / Phase E5 Sprint 2 — PT + LWF require a state.
        const body = {
            period_label: period,
            payroll_run_ids: selectedRuns,
        };
        if (form === "pt") body.state = ptState;
        if (form === "lwf") body.state = lwfState;
        setLoading(true);
        try {
            const { data } = await api.post(`/compliance/${form}/preview`, body);
            setPreview({ form, data });
            setPreviewOpen(true);
        } catch (err) {
            toast.error(formatApiError(err, "Preview failed"));
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async (form) => {
        if (!period || selectedRuns.length === 0) {
            toast.error("Set period and select payroll runs first");
            return;
        }
        const body = {
            period_label: period,
            payroll_run_ids: selectedRuns,
            note: `${form.toUpperCase()} via compliance console`,
        };
        if (form === "pt") body.state = ptState;
        if (form === "lwf") body.state = lwfState;
        setLoading(true);
        try {
            const { data } = await api.post(`/compliance/${form}/generate`, body);
            toast.success(`${data.form_type} generated · ${data.n_members} members · ${fmtMoney(data.total_remittance)}`);
            await downloadFiling(data.id, data.file_name);
            reload();
            setPreviewOpen(false);
        } catch (err) {
            toast.error(formatApiError(err, "Generate failed"));
        } finally {
            setLoading(false);
        }
    };

    const downloadFiling = async (filingId, fileName) => {
        try {
            const r = await api.get(`/compliance/filings/${filingId}/download`, { responseType: "blob" });
            const blob = new Blob([r.data], { type: r.headers["content-type"] });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName || `${filingId}.dat`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err, "Download failed"));
        }
    };

    const sealFiling = async (filingId) => {
        if (!window.confirm("Seal this filing? TERMINAL action — 4-eye rule: you cannot be the same actor who generated it.")) return;
        try {
            const { data } = await api.post(`/compliance/filings/${filingId}/seal`);
            toast.success(`Sealed · ${data.status}`);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Seal failed"));
        }
    };

    const renderSummary = () => {
        if (!summary) return null;
        const settingsOk = summary.settings_complete;
        return (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="compliance-summary">
                <Card title="Filings (total)" value={summary.n_total} tone="bg-slate-50" />
                <Card title="Generated" value={summary.n_generated} tone="bg-amber-50" />
                <Card title="Sealed" value={summary.n_sealed} tone="bg-emerald-50" />
                <Card
                    title="Settings"
                    value={settingsOk ? "Configured" : "Incomplete"}
                    tone={settingsOk ? "bg-emerald-50" : "bg-rose-50"}
                />
            </div>
        );
    };

    const renderBuilder = (form) => (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded p-4 bg-slate-50/30 space-y-3">
                    <h3 className="font-semibold text-sm">Build {form.toUpperCase()} Filing</h3>
                    {/* PT + LWF require a State dropdown */}
                    {(form === "pt" || form === "lwf") && (
                        <div>
                            <Label className="text-xs">State</Label>
                            <select
                                value={form === "pt" ? ptState : lwfState}
                                onChange={(e) => (form === "pt" ? setPtState(e.target.value) : setLwfState(e.target.value))}
                                className="border rounded px-2 py-1 text-sm w-full font-mono"
                                data-testid={`${form}-state-select`}
                            >
                                {(form === "pt" ? PT_STATES : LWF_STATES).map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div>
                        <Label className="text-xs">Period (YYYY-MM)</Label>
                        <Input
                            type="text"
                            value={period}
                            onChange={(e) => setPeriod(e.target.value)}
                            placeholder="2026-01"
                            className="font-mono"
                            data-testid={`${form}-period-input`}
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Payroll Runs (select 1 or more)</Label>
                        <div className="border rounded max-h-60 overflow-y-auto bg-white" data-testid={`${form}-runs-list`}>
                            {eligibleRuns.length === 0 ? (
                                <div className="text-xs text-slate-400 p-3 text-center">No eligible payroll runs (need Closed / Approved / Paid status)</div>
                            ) : eligibleRuns.map((r) => (
                                <label key={r.id} className="flex items-start gap-2 p-2 border-b text-xs hover:bg-slate-50 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectedRuns.includes(r.id)}
                                        onChange={() => toggleRun(r.id)}
                                        data-testid={`${form}-run-${r.id}`}
                                    />
                                    <div className="flex-1">
                                        <div className="font-mono">{r.id}</div>
                                        <div className="text-slate-500">{r.label || `${r.period_start} → ${r.period_end}`} · {r.status}</div>
                                    </div>
                                    {r.total_net_pay != null && (
                                        <div className="text-[10px] text-slate-400 font-mono">{fmtMoney(r.total_net_pay)}</div>
                                    )}
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button size="sm" variant="outline" disabled={loading} onClick={() => handlePreview(form)} data-testid={`${form}-preview-btn`}>
                            <Eye size={14} className="mr-1" /> Preview
                        </Button>
                        {canAdmin && (
                            <Button size="sm" disabled={loading} onClick={() => handleGenerate(form)} data-testid={`${form}-generate-btn`}>
                                <Sparkle size={14} className="mr-1" /> Generate & Download
                            </Button>
                        )}
                    </div>
                </div>

                <div className="border rounded p-4 bg-slate-50/30 text-xs space-y-2">
                    <h3 className="font-semibold text-sm">
                        {form === "pf" ? "PF ECR — D32+D33"
                            : form === "esi" ? "ESI Return — D32+D33"
                                : form === "pt" ? "PT Challan — D34"
                                    : "LWF Return — D35"}
                    </h3>
                    {form === "pf" && (
                        <>
                            <p>EPFO 2017 ECR text-file format · pipe-delimited · 11 columns.</p>
                            <ul className="list-disc pl-5 space-y-0.5">
                                <li>EE 12% · ER EPS 8.33% (capped ₹1,250) · ER EPF balance · EDLI 0.5% · Admin 0.5%</li>
                                <li>Statutory wage ceiling ₹15,000/m unless `pf_on_full_wages` enabled</li>
                                <li><strong>D33:</strong> missing UAN → warning only; export proceeds, operator corrects + re-exports</li>
                            </ul>
                        </>
                    )}
                    {form === "esi" && (
                        <>
                            <p>ESIC monthly contribution CSV · 6 columns.</p>
                            <ul className="list-disc pl-5 space-y-0.5">
                                <li>EE 0.75% · ER 3.25% · Wage ceiling ₹21,000/m</li>
                                <li>Out-of-scope employees excluded from CSV</li>
                                <li>Annual bonus / LTA / Gratuity / Leave Encashment excluded from ESI wages</li>
                            </ul>
                        </>
                    )}
                    {form === "pt" && (
                        <>
                            <p>State Professional Tax challan · per-state CSV format.</p>
                            <ul className="list-disc pl-5 space-y-0.5">
                                <li>Engine reads PT from <code>payroll_run_lines.deductions</code> · NEVER recomputes (single source of truth)</li>
                                <li><strong>Cadence:</strong> Tamil Nadu is half-yearly (Sep + Mar only); all others monthly</li>
                                <li><strong>D34-A:</strong> Maharashtra Feb adjustment (₹100 extra) baked in via <code>pt_period_adjustments_by_state</code></li>
                                <li><strong>D34-B:</strong> MH women ≤ ₹25k/m exempt via <code>pt_gender_exemption_by_state</code></li>
                            </ul>
                        </>
                    )}
                    {form === "lwf" && (
                        <>
                            <p>State Labour Welfare Fund return · per-state CSV format.</p>
                            <ul className="list-disc pl-5 space-y-0.5">
                                <li>Engine reads LWF_EE / LWF_ER from line · NEVER recomputes</li>
                                <li><strong>Cadence:</strong> MH + Gujarat half-yearly (Jun + Dec); KA + TN + AP annual (Dec)</li>
                                <li>Off-cycle months show ₹0 (cycle-close deduction model)</li>
                            </ul>
                        </>
                    )}
                </div>
            </div>
        </div>
    );

    const renderHistory = () => {
        const formMap = { pf: "PF_ECR", esi: "ESI_RETURN", pt: "PT_CHALLAN", lwf: "LWF_RETURN" };
        const targetForm = formMap[tab];
        const filtered = history.filter((h) => h.form_type === targetForm);
        return (
            <div className="border rounded mt-4">
                <div className="p-2 bg-slate-50 border-b font-semibold text-sm">Filing History · {filtered.length}</div>
                <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b uppercase text-slate-600">
                        <tr>
                            <th className="text-left px-2 py-1">Filing ID</th>
                            <th className="text-left px-2 py-1">Period</th>
                            <th className="text-right px-2 py-1">Members</th>
                            <th className="text-right px-2 py-1">Remittance</th>
                            <th className="text-left px-2 py-1">Status</th>
                            <th className="text-left px-2 py-1">Generated</th>
                            <th className="text-left px-2 py-1">By</th>
                            <th className="text-left px-2 py-1">Bytes Hash</th>
                            <th className="text-right px-2 py-1">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr><td colSpan={9} className="text-center py-4 text-slate-400">No filings yet</td></tr>
                        ) : filtered.map((h) => (
                            <tr key={h.id} className="border-b hover:bg-slate-50/50" data-testid={`compliance-row-${h.id}`}>
                                <td className="px-2 py-1 font-mono">{h.id}</td>
                                <td className="px-2 py-1">{h.period_label}</td>
                                <td className="px-2 py-1 text-right font-mono">{h.n_members}</td>
                                <td className="px-2 py-1 text-right font-mono">{fmtMoney(h.total_remittance)}</td>
                                <td className="px-2 py-1">
                                    <Badge className={`text-[10px] ${h.status === "Sealed" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{h.status}</Badge>
                                </td>
                                <td className="px-2 py-1 text-[10px]">{(h.generated_at || "").slice(0, 19).replace("T", " ")}</td>
                                <td className="px-2 py-1 text-[10px]">{h.generated_by_email}</td>
                                <td className="px-2 py-1 font-mono text-[10px]" title={h.file_bytes_hash}>{truncHash(h.file_bytes_hash)}</td>
                                <td className="px-2 py-1 text-right space-x-1">
                                    <Button variant="outline" size="sm" onClick={() => downloadFiling(h.id, h.file_name)} data-testid={`compliance-download-${h.id}`}>
                                        <DownloadSimple size={12} />
                                    </Button>
                                    {canSeal && h.status === "Generated" && (
                                        <Button variant="outline" size="sm" onClick={() => sealFiling(h.id)} data-testid={`compliance-seal-${h.id}`}>
                                            <ShieldCheck size={12} />
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderSettings = () => (
        <SettingsTab
            key={settings?.updated_at || "init"}
            settings={settings || {}}
            canAdmin={canAdmin}
            onSave={async (payload) => {
                try {
                    await api.put("/compliance/settings", payload);
                    toast.success("Settings saved");
                    reload();
                } catch (err) {
                    toast.error(formatApiError(err, "Save failed"));
                }
            }}
        />
    );

    const renderCoverage = () => (
        <CoverageTab
            coverage={coverage}
            canAdmin={canAdmin}
            onReload={reload}
        />
    );

    return (
        <div className="space-y-4" data-testid="compliance-console">
            <PageHeader
                title="Compliance & Statutory Reporting"
                subtitle="PF ECR · ESI Return · monthly statutory filings. Every filing carries a 4-hash D29-mirror seal + 4-eye approval before terminal sealing."
                actions={
                    <Button variant="outline" size="sm" onClick={reload} data-testid="compliance-refresh-btn">
                        <ArrowsClockwise size={14} className="mr-1" /> Refresh
                    </Button>
                }
            />

            {renderSummary()}

            <div className="flex border rounded overflow-hidden">
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        data-testid={`compliance-tab-${t.key}`}
                        className={`px-4 py-1.5 text-sm capitalize ${
                            tab === t.key
                                ? "bg-slate-900 text-white"
                                : "bg-white text-slate-700 hover:bg-slate-50"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "pf" && (<><div>{renderBuilder("pf")}</div>{renderHistory()}</>)}
            {tab === "esi" && (<><div>{renderBuilder("esi")}</div>{renderHistory()}</>)}
            {tab === "pt" && (<><div>{renderBuilder("pt")}</div>{renderHistory()}</>)}
            {tab === "lwf" && (<><div>{renderBuilder("lwf")}</div>{renderHistory()}</>)}
            {tab === "settings" && renderSettings()}
            {tab === "coverage" && renderCoverage()}

            {/* Preview dialog */}
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{preview?.form?.toUpperCase()} Preview · {preview?.data?.period_label}</DialogTitle>
                        <DialogDescription className="text-xs">
                            Read-only computation preview. No filing record is persisted until you click Generate.
                        </DialogDescription>
                    </DialogHeader>
                    {preview && (
                        <div className="text-xs space-y-3">
                            <div className="grid grid-cols-3 gap-3">
                                {Object.entries(preview.data.totals).map(([k, v]) => (
                                    <div key={k} className="border rounded p-2 bg-slate-50">
                                        <div className="text-[9px] uppercase text-slate-500">{k.replace(/_/g, " ")}</div>
                                        <div className="font-mono font-bold">{fmtMoney(v)}</div>
                                    </div>
                                ))}
                            </div>
                            {preview.data.n_members_no_uan > 0 && (
                                <div className="bg-amber-50 border border-amber-300 p-2 rounded text-amber-800 flex items-center gap-2">
                                    <Warning size={14} /> <strong>{preview.data.n_members_no_uan}</strong> member(s) missing UAN — will be exported with empty UAN field. Operator must correct + re-export.
                                </div>
                            )}
                            {preview.data.n_no_ip_number > 0 && (
                                <div className="bg-amber-50 border border-amber-300 p-2 rounded text-amber-800 flex items-center gap-2">
                                    <Warning size={14} /> <strong>{preview.data.n_no_ip_number}</strong> member(s) missing ESIC IP — will be flagged.
                                </div>
                            )}
                            {preview.data.n_out_of_scope > 0 && (
                                <div className="bg-slate-50 border border-slate-200 p-2 rounded text-slate-700 flex items-center gap-2">
                                    <CheckCircle size={14} /> <strong>{preview.data.n_out_of_scope}</strong> employee(s) above ESI ceiling — excluded from CSV.
                                </div>
                            )}
                            <table className="w-full text-[10px] border">
                                <thead className="bg-slate-100">
                                    <tr>
                                        <th className="text-left px-1 py-0.5">Emp Code</th>
                                        <th className="text-left px-1 py-0.5">Name</th>
                                        <th className="text-left px-1 py-0.5">{preview.form === "pf" ? "UAN" : preview.form === "esi" ? "IP #" : "PAN"}</th>
                                        {preview.form === "pf" && (
                                            <>
                                                <th className="text-right px-1 py-0.5">EPF Wages</th>
                                                <th className="text-right px-1 py-0.5">EE 12%</th>
                                                <th className="text-right px-1 py-0.5">EPS 8.33%</th>
                                                <th className="text-right px-1 py-0.5">ER EPF</th>
                                                <th className="text-right px-1 py-0.5">NCP Days</th>
                                            </>
                                        )}
                                        {preview.form === "esi" && (
                                            <>
                                                <th className="text-right px-1 py-0.5">Wages</th>
                                                <th className="text-right px-1 py-0.5">EE</th>
                                                <th className="text-right px-1 py-0.5">ER</th>
                                                <th className="text-right px-1 py-0.5">Days</th>
                                                <th className="text-left px-1 py-0.5">Notes</th>
                                            </>
                                        )}
                                        {preview.form === "pt" && (
                                            <>
                                                <th className="text-right px-1 py-0.5">Gross Wages</th>
                                                <th className="text-right px-1 py-0.5">PT Amount</th>
                                                <th className="text-left px-1 py-0.5">Cycle</th>
                                            </>
                                        )}
                                        {preview.form === "lwf" && (
                                            <>
                                                <th className="text-right px-1 py-0.5">Gross</th>
                                                <th className="text-right px-1 py-0.5">EE</th>
                                                <th className="text-right px-1 py-0.5">ER</th>
                                                <th className="text-right px-1 py-0.5">Total</th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {(preview.data.lines || []).slice(0, 100).map((ln, i) => (
                                        <tr key={i} className="border-b">
                                            <td className="px-1 py-0.5 font-mono">{ln.employee_code}</td>
                                            <td className="px-1 py-0.5">{ln.member_name || ln.ip_name || ln.employee_name}</td>
                                            <td className="px-1 py-0.5 font-mono">{ln.uan || ln.ip_number || ln.pan || <span className="text-rose-600">—</span>}</td>
                                            {preview.form === "pf" && (
                                                <>
                                                    <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(ln.epf_wages)}</td>
                                                    <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(ln.epf_contribution_ee)}</td>
                                                    <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(ln.eps_contribution)}</td>
                                                    <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(ln.epf_eps_diff)}</td>
                                                    <td className="px-1 py-0.5 text-right font-mono">{ln.ncp_days}</td>
                                                </>
                                            )}
                                            {preview.form === "esi" && (
                                                <>
                                                    <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(ln.total_monthly_wages)}</td>
                                                    <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(ln.employee_contribution)}</td>
                                                    <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(ln.employer_contribution)}</td>
                                                    <td className="px-1 py-0.5 text-right font-mono">{ln.days}</td>
                                                    <td className="px-1 py-0.5 text-[9px]">{ln.out_of_scope ? "OOS" : (ln.warnings?.length ? "⚠" : "")}</td>
                                                </>
                                            )}
                                            {preview.form === "pt" && (
                                                <>
                                                    <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(ln.gross_wages)}</td>
                                                    <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(ln.pt_amount)}</td>
                                                    <td className="px-1 py-0.5 text-[9px]">{ln.period_label}</td>
                                                </>
                                            )}
                                            {preview.form === "lwf" && (
                                                <>
                                                    <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(ln.gross_wages)}</td>
                                                    <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(ln.lwf_ee)}</td>
                                                    <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(ln.lwf_er)}</td>
                                                    <td className="px-1 py-0.5 text-right font-mono">{fmtMoney(ln.lwf_total)}</td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
                        {canAdmin && (
                            <Button onClick={() => handleGenerate(preview.form)} data-testid="compliance-generate-from-preview-btn">
                                <Sparkle size={14} className="mr-1" /> Generate & Download
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Card({ title, value, tone }) {
    return (
        <div className={`rounded border p-3 ${tone || "bg-slate-50"}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{title}</div>
            <div className="font-bold text-base mt-0.5">{value}</div>
        </div>
    );
}

function SettingsTab({ settings, canAdmin, onSave }) {
    // `settings` arrives async after mount; consume it as the initial
    // form state once it's non-null. Parent passes a stable `key` to
    // force remount after save (which triggers a reload) so the form
    // always reflects the latest persisted values.
    const [form, setForm] = useState(settings || {});
    return (
        <div className="border rounded p-4 space-y-3 max-w-2xl" data-testid="compliance-settings-tab">
            <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                    <Label className="text-xs">EPFO Establishment ID</Label>
                    <Input value={form.epfo_establishment_id || ""} onChange={(e) => setForm({ ...form, epfo_establishment_id: e.target.value })} placeholder="MH/BAN/12345/000" data-testid="compliance-epfo-id" />
                </div>
                <div>
                    <Label className="text-xs">ESIC Employer Code</Label>
                    <Input value={form.esic_employer_code || ""} onChange={(e) => setForm({ ...form, esic_employer_code: e.target.value })} placeholder="17-digit" data-testid="compliance-esic-code" />
                </div>
                <div>
                    <Label className="text-xs">PAN</Label>
                    <Input value={form.pan || ""} onChange={(e) => setForm({ ...form, pan: e.target.value })} />
                </div>
                <div>
                    <Label className="text-xs">TAN</Label>
                    <Input value={form.tan || ""} onChange={(e) => setForm({ ...form, tan: e.target.value })} />
                </div>
                <div>
                    <Label className="text-xs">Signatory Name</Label>
                    <Input value={form.signatory_name || ""} onChange={(e) => setForm({ ...form, signatory_name: e.target.value })} />
                </div>
                <div>
                    <Label className="text-xs">Signatory Designation</Label>
                    <Input value={form.signatory_designation || ""} onChange={(e) => setForm({ ...form, signatory_designation: e.target.value })} />
                </div>
                <div className="col-span-2">
                    <label className="flex items-center gap-2 text-xs">
                        <input type="checkbox" checked={!!form.epfo_pf_on_full_wages} onChange={(e) => setForm({ ...form, epfo_pf_on_full_wages: e.target.checked })} data-testid="compliance-pf-full-wages" />
                        <span>Contribute PF on full Basic+DA (uncapped — for operators who voluntarily pay above ₹15,000/m ceiling)</span>
                    </label>
                </div>
            </div>
            {canAdmin && (
                <Button onClick={() => onSave(form)} data-testid="compliance-settings-save-btn">
                    <FloppyDisk size={14} className="mr-1" /> Save Settings
                </Button>
            )}
        </div>
    );
}

function CoverageTab({ coverage, canAdmin, onReload }) {
    if (!coverage) return <div className="p-6 text-center text-slate-400">Loading…</div>;
    return (
        <div className="space-y-3" data-testid="compliance-coverage-tab">
            <div className="grid grid-cols-3 gap-3">
                <Card title="Active Employees" value={coverage.total_active_employees} tone="bg-slate-50" />
                <Card title="UAN Coverage" value={`${coverage.with_uan} / ${coverage.total_active_employees} (${coverage.uan_coverage_pct}%)`} tone={coverage.uan_coverage_pct === 100 ? "bg-emerald-50" : "bg-amber-50"} />
                <Card title="ESIC Coverage" value={`${coverage.with_esic_ip_number} / ${coverage.total_active_employees} (${coverage.esic_coverage_pct}%)`} tone={coverage.esic_coverage_pct === 100 ? "bg-emerald-50" : "bg-amber-50"} />
            </div>
            <div className="text-xs text-slate-600">
                Per-employee UAN / ESIC IP / PF Account # are set via the Employee Detail page → Statutory IDs section (or via API <code>/api/compliance/employee-statutory/{`{id}`}</code>).
            </div>
        </div>
    );
}
